import { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Leaf } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import PasswordChangeModal from "@/components/PasswordChangeModal";

export default function RoleLogin({ role, portalLabel, dashboardPath, testPrefix }) {
  const { login, user } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [forcedModal, setForcedModal] = useState(false);

  useEffect(() => {
    if (user?.role === role) {
      if (user.must_change_password) setForcedModal(true);
      else navigate(dashboardPath);
    }
  }, [user, role, dashboardPath, navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    const result = await login(email, password, role);
    setLoading(false);
    if (result.success) {
      if (result.user.must_change_password) {
        setForcedModal(true);
      } else {
        navigate(dashboardPath);
      }
    } else {
      setError(result.error);
    }
  };

  return (
    <div className="flex min-h-screen flex-col bg-slate-50">
      <header className="border-b border-slate-200 bg-white px-4 py-4 sm:px-6">
        <Link to="/" className="mx-auto flex max-w-6xl items-center gap-2">
          <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-teal-700 text-white">
            <Leaf className="h-5 w-5" />
          </div>
          <span className="text-lg font-semibold text-slate-900">RootLyst</span>
          <span className="ml-2 rounded-full bg-teal-50 px-2.5 py-0.5 text-xs font-medium text-teal-700">
            {portalLabel}
          </span>
        </Link>
      </header>
      <div className="flex flex-1 items-center justify-center px-4 py-12">
        <div className="w-full max-w-md card-surface card-surface-hover rounded-xl border border-slate-200 bg-white p-8 shadow-sm">
          <div className="mb-6">
            <h1 className="text-lg font-semibold text-slate-900">Sign in to your account</h1>
            <p className="mt-1 text-sm text-slate-500">Enter the credentials provided to you.</p>
          </div>
          <form onSubmit={handleSubmit} className="space-y-4" data-testid={`${testPrefix}-login-form`}>
            <div>
              <Label htmlFor="email">Email</Label>
              <Input
                data-testid={`${testPrefix}-login-email`}
                id="email"
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="mt-1"
              />
            </div>
            <div>
              <Label htmlFor="password">Password</Label>
              <Input
                data-testid={`${testPrefix}-login-password`}
                id="password"
                type="password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="mt-1"
              />
            </div>
            {error && (
              <p data-testid={`${testPrefix}-login-error`} className="text-sm text-red-600">
                {error}
              </p>
            )}
            <Button
              data-testid={`${testPrefix}-login-submit-btn`}
              type="submit"
              disabled={loading}
              className="w-full bg-teal-700 hover:bg-teal-800"
            >
              {loading ? "Signing in..." : "Sign In"}
            </Button>
          </form>
          <Link to="/" className="mt-6 block text-center text-xs text-slate-400 hover:text-slate-600">
            Back to RootLyst
          </Link>
        </div>
      </div>
      <PasswordChangeModal
        open={forcedModal}
        onOpenChange={setForcedModal}
        forced
        onSuccess={() => navigate(dashboardPath)}
      />
    </div>
  );
}
